import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Loader from '../Loader/Loader';
import RangeGraph from './Graph/RangeGraph';

import Filters from '../FiltersSet/Filters';
import DateSelector from '../DateSelector/DateSelector';
import Card from '../Card/Card';
import StatFilter from './Filter/StatFilter';
import ViewTitle from '../ViewTitle/ViewTitle';
import getNewRoute from '../../containers/helpers';
import { getRangeGraphData, useRangeGraphDataQuery } from '../../services/api';
import getDataInRange from '../../selectors/graph';

const RangeView = ({
  dateRange,
  defaultActiveFilters,
  colors,
  statsStyles,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const activeFilters = useMemo(
    () =>
      JSON.parse(searchParams?.get('activeFilters')) ?? defaultActiveFilters,
    [defaultActiveFilters, searchParams],
  );

  const statisticDisplay = useMemo(
    () =>
      JSON.parse(searchParams?.get('statisticDisplay')) || {
        percentile: 20,
      },
    [searchParams],
  );

  const graphOnly = useMemo(
    () => ['true', ''].includes(searchParams?.get('graphOnly')),
    [searchParams],
  );

  const optionsData = useMemo(
    () => ({ percentile: statisticDisplay.percentile }),
    [statisticDisplay.percentile],
  );

  const { data: graphData, isLoading: loadingGraphData } =
    useRangeGraphDataQuery(activeFilters, optionsData);

  const nextDateRange = useMemo(() => {
    const defaultValues = {
      start: 'begin',
      end: 'end',
      min: graphData?.dates?.[0],
      max: graphData?.dates?.[graphData?.dates.length - 1],
    };
    const urlDateRange =
      JSON.parse(searchParams?.get('dateRange')) ?? dateRange;
    return urlDateRange ? { ...defaultValues, ...urlDateRange } : defaultValues;
  }, [dateRange, graphData?.dates, searchParams]);

  const addVisualization = useCallback(() => {
    const filters = [...activeFilters];
    filters.push(filters[filters.length - 1]);
    navigate(getNewRoute(location, { activeFilters: filters }));
  }, [activeFilters, location, navigate]);

  /**
   * Remove a serie of the current filters
   * and update the route with the new filters
   *
   * @param {number} index the index of the filter to be removed
   *
   * @memberOf GenericView
   */
  const removeVisualization = useCallback(
    index => {
      const filters = [...activeFilters];
      filters.splice(index, 1);
      navigate(getNewRoute(location, { activeFilters: filters }));
    },
    [activeFilters, location, navigate],
  );

  const updateDateRange = useCallback(
    (start, end) => {
      const nextStart = (date => (date === nextDateRange.min ? 'begin' : date))(
        start ?? nextDateRange.start,
      );

      const nextEnd = (date => (date === nextDateRange.max ? 'end' : date))(
        end ?? nextDateRange.end,
      );

      navigate(
        getNewRoute(location, {
          dateRange: {
            start: nextStart,
            end: nextEnd,
          },
        }),
      );
    },
    [nextDateRange, location, navigate],
  );

  const updateStatDisplay = useCallback(
    (statisticName, value) => {
      const newStatistics = {
        ...statisticDisplay,
        [statisticName]: value,
      };
      navigate(getNewRoute(location, { statisticDisplay: newStatistics }));
    },
    [location, navigate, statisticDisplay],
  );

  if (loadingGraphData && !activeFilters.length)
    return <Loader>Loading filters...</Loader>;

  let FiltersView = <Loader />;
  let RangeGraphView = <Loader />;
  let GraphDateSelectorView = <Loader />;
  let StatsFiltersView = <Loader />;

  if (!loadingGraphData && !graphData?.dates?.length) {
    RangeGraphView = (
      <Card className="alert-danger warningCard">
        <div className="text-center">
          No data available for the selected filters
        </div>
      </Card>
    );
  }

  if (
    !loadingGraphData &&
    graphData?.dates?.length &&
    // api/filters no longer give the default data correctly
    activeFilters.some(({ stattype }) => stattype !== undefined)
  ) {
    RangeGraphView = (
      <RangeGraph
        activeFilters={activeFilters}
        dateRange={nextDateRange}
        data={getDataInRange(nextDateRange, graphData)}
        loading={loadingGraphData}
        color={colors}
        statsStyles={statsStyles}
        type="scatter"
        isExportable
        graphOnly={graphOnly}
        displayStat
        statisticDisplay={statisticDisplay}
        handleCSVExport={() =>
          getRangeGraphData(activeFilters, optionsData, {
            Accept: 'text/csv',
          })
        }
      />
    );
    GraphDateSelectorView = (
      <DateSelector
        activeFilters={activeFilters}
        graphData={graphData}
        loading={loadingGraphData}
        dateRange={nextDateRange}
        onChange={updateDateRange}
        color={colors}
        graphOnly={graphOnly}
        displayGraph
      />
    );
  }

  if (!loadingGraphData) {
    FiltersView = (
      <Filters
        addVisualization={addVisualization}
        filters={activeFilters}
        removeVisualization={removeVisualization}
        view="timeserie"
      />
    );
    StatsFiltersView = (
      <StatFilter
        statisticDisplay={statisticDisplay}
        updateStatDisplay={updateStatDisplay}
      />
    );
  }
  if (graphOnly || location.pathname.startsWith('/bulletin')) {
    return <div className="GraphView range">{RangeGraphView}</div>;
  }

  return (
    <>
      <ViewTitle pageTitle="Time Series" />
      <div className="GraphView range">
        <Card className="graph">
          {RangeGraphView}
          {GraphDateSelectorView}
        </Card>
        <Card className="filters">
          {FiltersView}
          {StatsFiltersView}
        </Card>
      </div>
    </>
  );
};

export default RangeView;
